/**
 * The lead the rep's Phone page works with: whose card, script and form are beside the phone. Pure,
 * so `test/phone-work.test.ts` pins the rules; `softphone/work-lead.ts` gathers what they are told.
 *
 * Several things can name a lead at once (the ACD's call, the outcome window after it, a manual
 * dial, the dialer placing a call, the dialer's offer), and they do not agree for a moment at every
 * change. One rule picks among them, and a second decides what stays on the page once nothing live
 * names anybody: a rep who has not finished the form when the call ends must still find it there,
 * and a lead who was only offered or dialed, and never spoken to, must not be left behind as one.
 */
import type { QueueSession } from "./types.js";

/** Where the page learned which lead is in front of the rep. */
export type WorkLeadSource = "call" | "window" | "manual" | "dial" | "offer";

export interface WorkLead {
  id: string;
  /** The caller's stay in the line, `useLeadCard`'s second key, when something names it. */
  stay: string | null;
  campaignId: string | null;
  from: WorkLeadSource;
}

export interface WorkLeadInput {
  /** The ACD's call, once it is matched to one of the phone's calls (`acd.callId !== null`),
   *  ringing or up. `leadId` null is an unknown caller. */
  call: { leadId: string | null; stay: string | null; campaignId: string | null } | null;
  /** The newest connected session of this rep, while the outcome window is open. Read from the
   *  raw session list, not `pendingDisposition`, so it survives the outcome being recorded. */
  window: { leadId: string | null; stay: string | null; campaignId: string | null } | null;
  /** A manual dial through a campaign, while `useManualCallWork().shown`. */
  manual: { leadId: string; campaignId: string | null } | null;
  /** The lead the dialer is calling (`useDialerCall`, else the recently dialed lead). */
  dial: { leadId: string; campaignId: string | null } | null;
  /** The dialer's preview offer. */
  offer: { leadId: string; campaignId: string | null } | null;
}

/** The lead something live names right now. A call or a window that names nobody ends the search:
 *  an unknown caller must not fall through to a stale offer. Order: call, window, manual, dial, offer. */
export function workLead(input: WorkLeadInput): WorkLead | null {
  for (const from of ["call", "window"] as const) {
    const named = input[from];
    if (named === null) continue;
    if (named.leadId === null) return null;
    return { id: named.leadId, stay: named.stay, campaignId: named.campaignId, from };
  }
  for (const from of ["manual", "dial", "offer"] as const) {
    const named = input[from];
    if (named !== null) return { id: named.leadId, stay: null, campaignId: named.campaignId, from };
  }
  return null;
}

/** What can name the lead a ringing call is for: the ACD's call, the rep's own manual dial coming
 *  back, or the dialer still placing the call that is now arriving. An offer says nothing about who
 *  is ringing, and neither does the outcome window of the call before. */
const AT_THE_PHONE: readonly WorkLeadSource[] = ["call", "manual", "dial"];

/** What names a lead before any call with them took place. A lead known only this way is not kept
 *  once nothing names it: a skipped offer and a dial nobody answered leave no form to finish. */
const BEFORE_A_CALL: readonly WorkLeadSource[] = ["offer", "dial"];

/**
 * The lead the page shows, and the one it keeps for later.
 *
 * Two facts stand in for the rep's record while it runs behind the phone, which it does at both
 * ends of every dialer call. `dialing` is the record saying the dialer is placing a call for the
 * rep: the invite usually reaches the browser before the record names the call, so a call that
 * arrives then is taken for the dial's. `sameCall` is the call at the phone being the very call the
 * kept lead was live on: the record can let go of a call before the phone hears its leg end.
 *
 *  - ringing: the work stays on screen only for the lead already kept, when the call, a manual
 *    dial or the dial names them, or when nothing names anybody while the kept lead came from the
 *    dial and the record still says dialing. Any other ring has the page to itself, and `kept`
 *    does not change until the ring is answered or gone.
 *  - a call is up: the live lead. With nothing live, the kept lead if it came from the dial and
 *    the record still says dialing (answered before the record named it), or, no longer live, if
 *    this is the call they were on. Otherwise nobody, and nothing kept: an unknown caller or a
 *    direct call must never sit over another lead's form.
 *  - nothing at the phone: the live lead. With nothing live, a lead no call took place with (an
 *    offer skipped or lapsed, a dial nobody answered) is dropped; any other kept lead stays, no
 *    longer live, until another lead arrives or the rep closes it.
 *
 * Between a flicker and the wrong lead's form under a call, the flicker is the lesser harm, so
 * every doubt above is settled by showing nothing.
 */
export function pageLead(state: {
  kept: WorkLead | null;
  live: WorkLead | null;
  ringing: boolean;
  onCall: boolean;
  /** The rep's record says the dialer is placing a call for them. */
  dialing: boolean;
  /** The call at the phone is the one the kept lead was live on. */
  sameCall: boolean;
}): { lead: WorkLead | null; live: boolean; kept: WorkLead | null } {
  const { kept, live, ringing, onCall, dialing, sameCall } = state;
  // The dialer's own call, which the record has not caught up with (or whose lead is being read).
  const dialed = live === null && kept !== null && kept.from === "dial" && dialing;
  if (ringing) {
    const same =
      live !== null && kept !== null && live.id === kept.id && AT_THE_PHONE.includes(live.from);
    if (same) return { lead: live, live: true, kept: live };
    return dialed ? { lead: kept, live: true, kept } : { lead: null, live: false, kept };
  }
  if (live !== null) return { lead: live, live: true, kept: live };
  if (dialed) return { lead: kept, live: true, kept };
  if (onCall) {
    return sameCall && kept !== null
      ? { lead: kept, live: false, kept }
      : { lead: null, live: false, kept: null };
  }
  if (kept !== null && BEFORE_A_CALL.includes(kept.from)) return { lead: null, live: false, kept: null };
  return { lead: kept, live: false, kept };
}

/** Whether two readings name the same lead the same way, so the store is not written for nothing. */
export function sameWorkLead(a: WorkLead | null, b: WorkLead | null): boolean {
  if (a === null || b === null) return a === b;
  return a.id === b.id && a.stay === b.stay && a.campaignId === b.campaignId && a.from === b.from;
}

/**
 * The call an outcome window is about: the rep's newest queue session that reached them, with or
 * without an outcome. `pendingDisposition` finds the same session and lets go of it the moment the
 * outcome is recorded; the page still shows that lead's form then. Matched on the realm as well as
 * the extension, for the reason given there.
 */
export function windowSession(
  sessions: readonly QueueSession[],
  agent: { user: string; domain: string } | null,
): QueueSession | null {
  if (!agent) return null;
  return (
    sessions.find(
      (s) =>
        s.agent_user === agent.user && s.agent_domain === agent.domain && s.bridge_started_at !== null,
    ) ?? null
  );
}

/** Whether the rep left "On this call" open. This browser only, like the folded sidebar: how much
 *  of the page the roster may take is the rep's own habit. Absent means hidden. */
const PEOPLE_KEY = "acl.phone.people";

export function readPeopleShown(): boolean {
  try {
    return localStorage.getItem(PEOPLE_KEY) === "shown";
  } catch {
    return false;
  }
}

export function writePeopleShown(shown: boolean): void {
  try {
    if (shown) localStorage.setItem(PEOPLE_KEY, "shown");
    else localStorage.removeItem(PEOPLE_KEY);
  } catch {
    /* non-fatal: the roster simply starts hidden after a reload */
  }
}

// --- the missed calls a rep has looked at --------------------------------------------------------------

/**
 * How far down "Missed calls" this account has read, in this browser: the time of the newest call
 * that was in the list when they last had it open (`newestMissed` in `lib/phone-page.ts`). The
 * rail's button counts the calls after it. Kept with the account it belongs to (`accountScope`),
 * because two people can use one browser ("Sign in as"), and one's mark must not hide the other's
 * calls. One key, the last reader's: coming back after somebody else counts the week's calls once.
 */
const MISSED_SEEN_KEY = "acl.phone.missed-seen";

/** A stored mark, read back: the time, when it is this account's and is a time at all. */
export function parseMissedSeen(raw: string | null, account: string): string | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const { account: whose, at } = value as Record<string, unknown>;
    if (whose !== account || typeof at !== "string" || Number.isNaN(Date.parse(at))) return null;
    return at;
  } catch {
    return null;
  }
}

export function readMissedSeen(account: string): string | null {
  try {
    return parseMissedSeen(localStorage.getItem(MISSED_SEEN_KEY), account);
  } catch {
    return null;
  }
}

export function writeMissedSeen(account: string, at: string): void {
  try {
    localStorage.setItem(MISSED_SEEN_KEY, JSON.stringify({ account, at }));
  } catch {
    /* non-fatal: the button simply counts the list again after a reload */
  }
}

// --- the panes beside the call's card --------------------------------------------------------------------

/**
 * Which panes a rep has beside the call's card: the sheet they fill in (the form or the intake)
 * and the one they read (the script, the lead's texts or the call's live transcript). The card is
 * always there; these are theirs to switch on and off, so the page is the card alone, the card
 * and one of them, or the card with one of each side by side.
 */
export interface PaneChoice {
  form: boolean;
  script: boolean;
  /** The intake's questions (ACL-403). */
  intake: boolean;
  /** The lead's texts and the pictures they sent (ACL-561). */
  texts: boolean;
  /** The call's live transcript, in the script's place. */
  transcript: boolean;
}

/**
 * The intake is up to begin with; the form and the script come when the rep asks.
 *
 * The intake starts on because on a call it is the work — a rep who had to find a switch first
 * would be part-way through the call before the first question was on screen. It is also why the
 * *form* no longer starts on: the two cannot share the page (`togglePane`), so a default with
 * both would have put one of them away on the first render and looked like a bug.
 *
 * A rep who wants the form instead switches to it, and that choice is remembered. The texts come
 * when asked for, or by themselves when the lead texts during the call (`textArrival`).
 *
 * A call itself now opens on the form, whatever is stored here (ACL-573, `routes/phone.tsx`): the
 * form's Required Intake and Full Intake folders are what reps are asked to have up while the lead
 * is talking. This is still the page's choice before that, and the one a rep's press is saved into.
 */
export const DEFAULT_PANES: PaneChoice = { form: false, script: false, intake: true, texts: false, transcript: false };

/** This browser only, like the folded sidebar and "On this call": how the page is laid out is a
 *  matter of the screen it is on. */
const PANES_KEY = "acl.phone.panes";

/**
 * A stored choice, read back. Anything that is not the two switches as written here (an older
 * shape, somebody's edit, nothing at all) is the default for the part that cannot be read, so a
 * bad value never leaves a rep with a page they cannot get their form back on.
 */
export function parsePanes(raw: string | null): PaneChoice {
  if (!raw) return DEFAULT_PANES;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return DEFAULT_PANES;
    // Per key, so a choice stored before a pane existed keeps the rest and takes the default
    // for the new one rather than resetting the lot.
    const { form, script, intake, texts, transcript } = value as Record<string, unknown>;
    const read = {
      form: typeof form === "boolean" ? form : DEFAULT_PANES.form,
      script: typeof script === "boolean" ? script : DEFAULT_PANES.script,
      intake: typeof intake === "boolean" ? intake : DEFAULT_PANES.intake,
      texts: typeof texts === "boolean" ? texts : DEFAULT_PANES.texts,
      transcript: typeof transcript === "boolean" ? transcript : DEFAULT_PANES.transcript,
    };
    // The intake and the form cannot share the page. A stored choice can still name both — it
    // was written before the intake existed (`form: true`, and the intake takes its default
    // above), or by an older build. The intake wins, because on a call it is the work. Settled
    // here rather than on screen, so the rail's two buttons never both read as pressed.
    if (read.intake && read.form) read.form = false;
    // The script, the texts and the live transcript take turns the same way. No build writes two
    // of them, so only an edit can: the script, which was there first, keeps its place, and the
    // texts come before the transcript.
    if (read.script && read.texts) read.texts = false;
    if ((read.script || read.texts) && read.transcript) read.transcript = false;
    return read;
  } catch {
    return DEFAULT_PANES;
  }
}

export function readPanes(): PaneChoice {
  try {
    return parsePanes(localStorage.getItem(PANES_KEY));
  } catch {
    return DEFAULT_PANES;
  }
}

export function writePanes(choice: PaneChoice): void {
  try {
    localStorage.setItem(
      PANES_KEY,
      JSON.stringify({
        form: choice.form, script: choice.script, intake: choice.intake, texts: choice.texts,
        transcript: choice.transcript,
      }),
    );
  } catch {
    /* non-fatal: the panes simply start as the default after a reload */
  }
}

/** The lead's newest text as far as the page has seen it during one call: which call and lead it
 *  was looking at (`key`), and when that text was. */
export interface TextsSeen {
  key: string;
  at: string | null;
}

/**
 * Whether the lead has texted since the page started looking during this call (ACL-561), and
 * what it has seen now. A lead who sends a picture while talking to the rep should not wait for
 * the rep to think of the Texts pane: the page brings it up.
 *
 * The first answer for a call is where it starts: what the lead sent before the call is already
 * history, so a call to somebody who texted yesterday does not open on the texts. After that, a
 * newer text from the lead is an arrival. A newer one from our side moves the mark and is not:
 * the rep sent it, or a cadence did, and nobody needs to be shown that.
 */
export function textArrival(
  seen: TextsSeen | null,
  key: string,
  last: { at: string; inbound: boolean } | null,
): { seen: TextsSeen; arrived: boolean } {
  if (seen === null || seen.key !== key) return { seen: { key, at: last?.at ?? null }, arrived: false };
  if (last === null || (seen.at !== null && Date.parse(last.at) <= Date.parse(seen.at))) {
    return { seen, arrived: false };
  }
  return { seen: { key, at: last.at }, arrived: last.inbound };
}
